import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Business as ProjectIcon,
  HourglassEmpty as HourglassIcon,
  Update as UpdateIcon,
  PersonOff as PersonOffIcon,
} from "@mui/icons-material";
import { ContractorLayout } from "../../components/layout/ContractorLayout";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { Project } from "./projectTypes";
import { MilestonesList } from "../../components/MilestonesList";
import { CreateMilestone } from "../../components/CreateMilestone";
import { MilestoneProgressBar } from "../../components/MilestoneProgressBar";
import ApprovalsList from "../../components/ApprovalsList";
import ChatWindow from "../../components/ChatWindow";
import DocumentUploader from "../../components/DocumentUploader";
import DocumentsList from "../../components/DocumentsList";
import { PlanningTab } from "../../components/planning/PlanningTab";
import { CreateUpdate } from "../../components/updates/CreateUpdate";
import { UpdatesList } from "../../components/updates/UpdatesList";
import { useChat } from "../../hooks/useChat";
import { useDocuments } from "../../hooks/useDocuments";
import { useUpdates } from "../../hooks/useUpdates";
import {
  Milestone,
  MilestoneFormData,
  MilestoneStatus,
} from "../../components/milestonesTypes";

interface ClientSummary {
  id: string;
  name: string | null;
  email: string;
}

const statusLabels: Record<Project["status"], string> = {
  planning: "Planning",
  in_progress: "In Progress",
  on_hold: "On Hold",
  complete: "Complete",
};

export function ContractorProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { profile: user } = useAuth();
  const [tab, setTab] = useState(0);
  const [project, setProject] = useState<Project | null>(null);
  const [client, setClient] = useState<ClientSummary | null>(null);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [milestones, setMilestones] = useState<Milestone[]>([]);

  const {
    updates,
    loading: updatesLoading,
    error: updatesError,
    createUpdate,
    deleteUpdate,
  } = useUpdates(projectId || "");
  const [createUpdateOpen, setCreateUpdateOpen] = useState(false);
  const [updateSaving, setUpdateSaving] = useState(false);
  const [updateSaveError, setUpdateSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [milestoneSaving, setMilestoneSaving] = useState(false);
  const [milestoneError, setMilestoneError] = useState<string | null>(null);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(
    null,
  );
  const [milestoneFormData, setMilestoneFormData] = useState<MilestoneFormData>(
    {
      title: "",
      targetDate: "",
      percentage: "",
      status: "not_started",
    },
  );
  const [deleteMilestoneOpen, setDeleteMilestoneOpen] = useState(false);
  const [milestoneToDelete, setMilestoneToDelete] = useState<Milestone | null>(
    null,
  );
  const [completeMilestoneOpen, setCompleteMilestoneOpen] = useState(false);
  const [milestoneToComplete, setMilestoneToComplete] =
    useState<Milestone | null>(null);
  const [completionFile, setCompletionFile] = useState<File | null>(null);

  // Chat hook
  const chat = useChat(
    projectId || "",
    user?.id || "",
    user?.name || "",
    "contractor",
  );

  // Documents hook
  const documents = useDocuments(projectId || "");

  const completionPercent = useMemo(() => {
    if (!milestones.length) return 0;
    const total = milestones.reduce((sum, milestone) => {
      if (milestone.status === "complete") {
        return sum + Number(milestone.percentage_of_project || 0);
      }
      return sum;
    }, 0);
    return Math.min(100, Math.round(total));
  }, [milestones]);

  const lastUpdate = updates[0];

  const fetchProjectData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);

    const { data: projectData, error: projectError } = await supabase
      .from("projects")
      .select("*")
      .eq("id", projectId)
      .single();

    if (projectError || !projectData) {
      setError(projectError?.message || "Project not found.");
      setLoading(false);
      return;
    }

    const { data: clientData } = await supabase
      .from("users")
      .select("id, name, email")
      .eq("id", projectData.client_id)
      .single();

    const { error: approvalError, count } = await supabase
      .from("approval_requests")
      .select("id", { count: "exact", head: true })
      .eq("project_id", projectId)
      .eq("status", "pending");

    if (approvalError) {
      setError(approvalError.message);
    }

    const { data: milestoneData, error: milestoneError } = await supabase
      .from("milestones")
      .select(
        "id, project_id, title, target_date, completion_date, status, milestone_order, percentage_of_project",
      )
      .eq("project_id", projectId)
      .order("milestone_order", { ascending: true });

    if (milestoneError) {
      setError(milestoneError.message);
    }

    setProject(projectData as Project);
    setClient((clientData as ClientSummary) || null);
    setPendingApprovals(count ?? 0);
    setMilestones((milestoneData ?? []) as Milestone[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  useEffect(() => {
    if (!projectId) return;

    const channel = supabase
      .channel(`project-detail-${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "projects",
          filter: `id=eq.${projectId}`,
        },
        () => {
          fetchProjectData();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchProjectData, projectId]);

  const resetMilestoneForm = () => {
    setMilestoneFormData({
      title: "",
      targetDate: "",
      percentage: "",
      status: "not_started",
    });
  };

  const openCreateMilestone = () => {
    setEditingMilestone(null);
    resetMilestoneForm();
    setMilestoneError(null);
    setMilestoneDialogOpen(true);
  };

  const openEditMilestone = (milestone: Milestone) => {
    setEditingMilestone(milestone);
    setMilestoneFormData({
      title: milestone.title,
      targetDate: milestone.target_date ?? "",
      percentage: milestone.percentage_of_project.toString(),
      status: milestone.status,
    });
    setMilestoneError(null);
    setMilestoneDialogOpen(true);
  };

  const handleMilestoneDialogClose = () => {
    setMilestoneDialogOpen(false);
    setMilestoneSaving(false);
    setMilestoneError(null);
  };

  const handleMilestoneSave = async (data: MilestoneFormData) => {
    if (!projectId) return;
    if (!data.title.trim()) {
      setMilestoneError("Milestone title is required.");
      return;
    }

    const percentageValue = Number(data.percentage);
    if (
      Number.isNaN(percentageValue) ||
      percentageValue < 0 ||
      percentageValue > 100
    ) {
      setMilestoneError("Percentage must be between 0 and 100.");
      return;
    }

    setMilestoneSaving(true);
    setMilestoneError(null);

    const milestoneOrder = editingMilestone
      ? editingMilestone.milestone_order
      : Math.max(0, ...milestones.map((item) => item.milestone_order)) + 1;

    const payload = {
      project_id: projectId,
      title: data.title.trim(),
      target_date: data.targetDate || null,
      status: data.status,
      milestone_order: milestoneOrder,
      percentage_of_project: percentageValue,
    };

    try {
      if (editingMilestone) {
        const { error: updateError } = await supabase
          .from("milestones")
          .update(payload)
          .eq("id", editingMilestone.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from("milestones")
          .insert(payload);

        if (insertError) throw insertError;
      }

      setMilestoneDialogOpen(false);
      await fetchProjectData();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to save.";
      setMilestoneError(message);
    } finally {
      setMilestoneSaving(false);
    }
  };

  const handleReorderMilestones = async (ordered: Milestone[]) => {
    setMilestones(ordered);
    const updates = await Promise.all(
      ordered.map((milestone, index) =>
        supabase
          .from("milestones")
          .update({ milestone_order: index + 1 })
          .eq("id", milestone.id),
      ),
    );

    const failed = updates.find((result) => result.error);
    if (failed?.error) {
      setMilestoneError(failed.error.message);
      fetchProjectData();
    }
  };

  const handleDeleteMilestone = (milestone: Milestone) => {
    setMilestoneToDelete(milestone);
    setDeleteMilestoneOpen(true);
  };

  const handleDeleteMilestoneClose = () => {
    setDeleteMilestoneOpen(false);
    setMilestoneToDelete(null);
  };

  const handleDeleteMilestoneConfirm = async () => {
    if (!milestoneToDelete) return;
    setMilestoneError(null);

    const { data, error: deleteError } = await supabase
      .from("milestones")
      .delete()
      .eq("id", milestoneToDelete.id)
      .select("id");

    if (deleteError) {
      setMilestoneError(deleteError.message);
      return;
    }

    if (!data || data.length === 0) {
      setMilestoneError("Delete was blocked by RLS.");
      return;
    }

    handleDeleteMilestoneClose();
    await fetchProjectData();
  };

  const openCompleteMilestone = (milestone: Milestone) => {
    setMilestoneToComplete(milestone);
    setCompletionFile(null);
    setCompleteMilestoneOpen(true);
  };

  const handleCompleteClose = () => {
    setCompleteMilestoneOpen(false);
    setMilestoneToComplete(null);
    setCompletionFile(null);
  };

  const handleCompleteConfirm = async () => {
    if (!milestoneToComplete) return;

    const { error: updateError } = await supabase
      .from("milestones")
      .update({
        status: "complete" as MilestoneStatus,
        completion_date: new Date().toISOString(),
      })
      .eq("id", milestoneToComplete.id);

    if (updateError) {
      setMilestoneError(updateError.message);
      return;
    }

    handleCompleteClose();
    await fetchProjectData();
  };

  if (loading) {
    return (
      <ContractorLayout>
        <Card>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Loading project...</Typography>
          </CardContent>
        </Card>
      </ContractorLayout>
    );
  }

  if (error || !project) {
    return (
      <ContractorLayout>
        <Alert severity="error">{error || "Unable to load project."}</Alert>
      </ContractorLayout>
    );
  }

  return (
    <ContractorLayout>
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/contractor")}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {project.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {project.address || "No address on file"}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Chip
            label={statusLabels[project.status]}
            color={
              project.status === "complete"
                ? "success"
                : project.status === "on_hold"
                  ? "warning"
                  : "primary"
            }
            variant="outlined"
          />
        </Box>

        <Card>
          <CardContent>
            <Tabs value={tab} onChange={(_, value) => setTab(value)}>
              <Tab label="Overview" />
              <Tab label="Planning" />
              <Tab label="Updates" />
              <Tab label="Approvals" />
              <Tab label="Milestones" />
              <Tab label="Chat" />
              <Tab label="Documents" />
            </Tabs>
            <Divider sx={{ my: 2 }} />

            {tab === 0 && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Project Overview
                      </Typography>
                      <Stack spacing={1.5}>
                        {!project.client_id && (
                          <Alert
                            severity="warning"
                            icon={<PersonOffIcon fontSize="small" />}
                            sx={{ py: 0.5 }}
                          >
                            Client account has been deleted. Their info is
                            preserved below for your records.
                          </Alert>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Client:{" "}
                          {client?.name ||
                            client?.email ||
                            project.client_name_snap ||
                            project.client_email_snap ||
                            "Unknown"}
                          {!project.client_id && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.disabled"
                              sx={{ ml: 0.5 }}
                            >
                              (account deleted)
                            </Typography>
                          )}
                        </Typography>
                        {!project.client_id && project.client_email_snap && (
                          <Typography variant="body2" color="text.secondary">
                            Client email: {project.client_email_snap}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Start date: {project.start_date || "Not set"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Target completion:{" "}
                          {project.estimated_completion_date || "Not set"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Budget:{" "}
                          {project.budget ? `$${project.budget}` : "Not set"}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Stack spacing={2}>
                    <Card variant="outlined">
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <ProjectIcon color="primary" />
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {completionPercent}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Completion
                            </Typography>
                          </Box>
                        </Stack>
                        <MilestoneProgressBar value={completionPercent} />
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <HourglassIcon color="warning" />
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {pendingApprovals}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Pending approvals
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <UpdateIcon color="success" />
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {lastUpdate?.title || "No updates"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Last update
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                </Grid>
              </Grid>
            )}

            {tab === 1 && (
              <PlanningTab projectId={project.id} />
            )}

            {tab === 2 && (
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Progress Updates
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setCreateUpdateOpen(true)}
                  >
                    Post update
                  </Button>
                </Box>
                <UpdatesList
                  updates={updates}
                  loading={updatesLoading}
                  error={updatesError}
                  canDelete
                  onDelete={deleteUpdate}
                />
              </Stack>
            )}

            {tab === 3 && (
              <ApprovalsList
                projectId={project.id}
                contractorId={user?.id || ""}
              />
            )}

            {tab === 4 && (
              <Stack spacing={2}>
                {milestoneError && (
                  <Alert severity="error">{milestoneError}</Alert>
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Milestones
                  </Typography>
                  <Button variant="contained" onClick={openCreateMilestone}>
                    Add milestone
                  </Button>
                </Box>
                <MilestoneProgressBar value={completionPercent} />
                <MilestonesList
                  milestones={milestones}
                  onEdit={openEditMilestone}
                  onDelete={handleDeleteMilestone}
                  onMarkComplete={openCompleteMilestone}
                  onReorder={handleReorderMilestones}
                />
              </Stack>
            )}

            {tab === 5 && (
              <Box
                sx={{
                  height: "600px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ChatWindow
                  projectId={projectId || ""}
                  currentUserId={user?.id || ""}
                  currentUserName={user?.name || ""}
                  messages={chat.messages}
                  typingUsers={chat.typingUsers}
                  loading={chat.loading}
                  initialLoading={chat.loading && chat.messages.length === 0}
                  error={chat.error}
                  hasMore={chat.hasMore}
                  onSendMessage={chat.sendMessage}
                  onLoadMore={chat.loadMoreMessages}
                  disabled={false}
                />
              </Box>
            )}

            {tab === 6 && (
              <Stack spacing={3}>
                <DocumentUploader
                  onUpload={(file, category) =>
                    documents.uploadDocument(file, category, user?.id || "")
                  }
                />
                <DocumentsList
                  documents={documents.documents}
                  loading={documents.loading}
                  error={documents.error}
                  currentUserId={user?.id || ""}
                  onDelete={documents.deleteDocument}
                />
              </Stack>
            )}
          </CardContent>
        </Card>

        {tab === 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Latest Updates
              </Typography>
              {updates.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No updates yet.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {updates.map((update) => (
                    <Box key={update.id}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {update.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {update.description || "No description"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(update.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>

      <CreateUpdate
        open={createUpdateOpen}
        saving={updateSaving}
        error={updateSaveError}
        onClose={() => {
          setCreateUpdateOpen(false);
          setUpdateSaveError(null);
        }}
        onSave={async (data) => {
          if (!user?.id) return;
          setUpdateSaving(true);
          setUpdateSaveError(null);
          try {
            await createUpdate(user.id, data);
            setCreateUpdateOpen(false);
          } catch (err) {
            setUpdateSaveError(
              err instanceof Error ? err.message : "Failed to post update.",
            );
          } finally {
            setUpdateSaving(false);
          }
        }}
      />

      <CreateMilestone
        open={milestoneDialogOpen}
        saving={milestoneSaving}
        error={milestoneError}
        initialData={milestoneFormData}
        onClose={handleMilestoneDialogClose}
        onSave={handleMilestoneSave}
      />

      <Dialog
        open={deleteMilestoneOpen}
        onClose={handleDeleteMilestoneClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete milestone</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Delete {milestoneToDelete?.title}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteMilestoneClose}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteMilestoneConfirm}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={completeMilestoneOpen}
        onClose={handleCompleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Complete milestone</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Upload a photo to document completion (optional).
            </Typography>
            <Button component="label" variant="outlined">
              Choose photo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(event) =>
                  setCompletionFile(event.target.files?.[0] ?? null)
                }
              />
            </Button>
            {completionFile && (
              <Typography variant="caption" color="text.secondary">
                Selected: {completionFile.name}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCompleteClose}>Cancel</Button>
          <Button variant="contained" onClick={handleCompleteConfirm}>
            Mark complete
          </Button>
        </DialogActions>
      </Dialog>
    </ContractorLayout>
  );
}
